import React, { useEffect, useState } from "react";
import { TID } from "@/constants/testIds";
import { Menu, X, Users } from "lucide-react";
import LogoMR from "@/components/landing/LogoMR";

const COMMUNITY_URL = "https://chat.whatsapp.com/BYtWElK92VpIFAHOAd1H4X";
const AMAZON_URL = "https://amzn.eu/d/0iLs59IO";

const links = [
  { id: "method", label: "Il Metodo", testid: TID.navMethod },
  { id: "author", label: "L'Autore", testid: TID.navAuthor },
  { id: "reviews", label: "Recensioni", testid: TID.navReviews },
  { id: "audiobook", label: "Audiolibro", testid: "nav-link-audiobook" },
  { id: "blog", label: "Blog", testid: "nav-link-blog" },
  { id: "contact", label: "Contatti", testid: "nav-link-contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    handler();
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      data-testid={TID.navRoot}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#F9F8F6]/85 border-b border-[#E5E1DA]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-5 flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="inline-flex items-center gap-2.5"
          data-testid="nav-brand"
        >
          <LogoMR size={40} />
          <span className="font-serif-display text-xl tracking-tight text-[#0B192C]">
            Metodo<span className="text-[#D4B895]">·</span>di<span className="text-[#D4B895]">·</span>Mare
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((link) => (
            <button
              key={link.id}
              data-testid={link.testid}
              onClick={() => scrollTo(link.id)}
              className="link-underline text-sm font-light text-[#0B192C] hover:text-[#1E3557]"
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="/area-iscritti"
            data-testid="nav-area-iscritti-btn"
            className="inline-flex items-center gap-2 px-3 py-3 border border-[#0E7C8F] text-[#0E7C8F] text-[10px] uppercase tracking-[0.16em] whitespace-nowrap hover:bg-[#0E7C8F] hover:text-[#F9F8F6] transition-colors"
          >
            Area Iscritti
          </a>
          <a
            href="/librerie"
            data-testid="nav-area-librerie-btn"
            className="inline-flex items-center gap-2 px-3 py-3 border border-[#D4B895] text-[#0B192C] bg-[#D4B895]/20 text-[10px] uppercase tracking-[0.16em] whitespace-nowrap hover:bg-[#D4B895] transition-colors"
          >
            Area Librerie
          </a>
          <a
            href="/consulenza"
            data-testid="nav-consulenza-btn"
            className="inline-flex items-center gap-2 px-3 py-3 border border-[#0B192C] text-[#0B192C] text-[10px] uppercase tracking-[0.16em] whitespace-nowrap hover:bg-[#E89B6C] hover:border-[#E89B6C] transition-colors"
          >
            Consulenza Aziendale e Professionisti
          </a>
          <a
            href="/certificazione"
            data-testid="nav-certificazione-btn"
            className="inline-flex items-center gap-2 px-4 py-3 border border-[#E89B6C] bg-[#E89B6C]/10 text-[#0B192C] text-xs uppercase tracking-[0.18em] hover:bg-[#E89B6C] transition-colors"
          >
            Diventa Certificato
          </a>
          <a
            href={AMAZON_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={TID.navCta}
            className="px-5 py-3 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.2em] hover:bg-[#1E3557] transition-colors"
          >
            Acquista
          </a>
        </div>

        <button
          className="md:hidden"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E5E1DA] bg-[#F9F8F6] px-6 py-6 space-y-4">
          {links.map((link) => (
            <button
              key={link.id}
              data-testid={`mobile-${link.testid}`}
              onClick={() => scrollTo(link.id)}
              className="block w-full text-left text-base"
            >
              {link.label}
            </button>
          ))}
          <a
            href={COMMUNITY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-community-btn"
            className="block w-full text-center mt-3 px-6 py-3 border border-[#0B192C] text-xs uppercase tracking-[0.18em]"
          >
            Entra nella Community
          </a>
          <a
            href="/certificazione"
            data-testid="mobile-nav-certificazione-btn"
            className="block w-full text-center px-6 py-3 border border-[#0B192C] text-xs uppercase tracking-[0.18em]"
          >
            Certificazione
          </a>
          <a
            href="/librerie"
            data-testid="mobile-nav-librerie-btn"
            className="block w-full text-center px-6 py-3 border border-[#D4B895] bg-[#D4B895]/20 text-xs uppercase tracking-[0.18em]"
          >
            Area Librerie
          </a>
          <a
            href="/consulenza"
            data-testid="mobile-nav-consulenza-btn"
            className="block w-full text-center px-6 py-3 border border-[#0B192C] text-xs uppercase tracking-[0.18em]"
          >
            Consulenza Aziendale e Professionisti
          </a>
          <a
            href={AMAZON_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-nav-purchase-btn"
            className="block w-full text-center px-6 py-3 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.2em]"
          >
            Acquista su Amazon
          </a>
        </div>
      )}
    </header>
  );
}
